import { useState, useRef, useEffect } from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Route as rootRoute } from './__root';
import { Button } from '../components/Button';
import { customersQueryOptions } from '../lib/queries/customers';
import { useDebounce } from '../hooks/useDebounce';
import {
  recentSessionsQueryOptions,
  type ImpersonateSession,
} from '../lib/queries/impersonate';
import { useStartSession } from '../lib/mutations/impersonate';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/impersonate',
  component: ImpersonatePage,
});

// helpers
function formatDate(iso: string) {
  const d = new Date(iso);
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  );
}

function formatDuration(ms: number | null) {
  if (ms == null) return null;
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m}m ${rem}s` : `${s}s`;
}

// ── tenant combobox ───────────────────────────────────────────────────────────

interface TenantOption {
  id: string;
  name: string;
}

function TenantCombobox({
  value,
  onChange,
}: {
  value: TenantOption | null;
  onChange: (t: TenantOption | null) => void;
}) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const search = useDebounce(input, 250);
  const ref = useRef<HTMLDivElement>(null);

  const { data } = useQuery(customersQueryOptions({ search, limit: 10 }));
  const options = data?.tenants ?? [];

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function select(t: TenantOption) {
    onChange(t);
    setInput(t.name);
    setOpen(false);
  }

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        value={value ? value.name : input}
        onChange={(e) => {
          setInput(e.target.value);
          onChange(null);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search tenants…"
        className="text-primary placeholder:text-primary/30 border-primary/20 focus:border-primary/50 w-full rounded border bg-transparent px-3 py-2 text-sm focus:outline-none"
      />
      {open && options.length > 0 && (
        <ul className="bg-bg-elevated border-primary/20 absolute z-10 mt-1 w-full rounded border shadow-lg">
          {options.map((t) => (
            <li
              key={t.id}
              onMouseDown={() => select({ id: t.id, name: t.name })}
              className="text-primary cursor-pointer px-3 py-2 text-sm hover:bg-white/6"
            >
              {t.name}
              <span className="text-muted ml-2 font-mono text-xs">{t.id}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// page

function ImpersonatePage() {
  const [tenant, setTenant] = useState<TenantOption | null>(null);
  const [reason, setReason] = useState('');
  const [ticketRef, setTicketRef] = useState('');

  const { data, isLoading } = useQuery(recentSessionsQueryOptions());

  // const sessions = data?.sessions ?? [];
  const sessions: ImpersonateSession[] = data?.sessions ?? [];

  const { mutate, isPending, isError } = useStartSession();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!tenant || !reason.trim()) return;
    mutate(
      {
        tenantId: tenant.id,
        reason: reason.trim(),
        ticketRef: ticketRef.trim(),
      },
      {
        onSuccess(data) {
          window.open(data.chatUrl, '_blank', 'noopener,noreferrer');
          setTenant(null);
          setReason('');
          setTicketRef('');
        },
      },
    );
  }
  const canSubmit = !!tenant && reason.trim().length > 0 && !isPending;

  return (
    <div className="space-y-6 p-8">
      {/* warning banner */}
      <div className="border-warning rounded-lg border px-5 py-4">
        <div className="text-warning mb-1 flex items-center gap-2 text-base font-bold">
          <AlertTriangle size={20} />
          This action is fully audit-logged and immutable
        </div>
        <p className="text-warning text-sm leading-relaxed">
          Session is strictly read-only — no actions can be taken as the tenant.
          An orange banner visible to anyone viewing the screen persists for the
          entire session duration. Session auto-expires after 30 minutes.
        </p>
      </div>

      {/* start session form */}
      <div className="bg-bg-elevated rounded-lg border border-white/6 p-6">
        <p className="text-subtle mb-5 text-xs tracking-widest uppercase">
          Start Session
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-primary/50 text-xs font-medium">
              Tenant
            </label>
            <TenantCombobox value={tenant} onChange={setTenant} />
          </div>

          <div className="space-y-1.5">
            <label className="text-primary/50 text-xs font-medium">
              Reason
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Tenant reported skill not rendering correctly"
              className="text-primary placeholder:text-primary/30 border-primary/20 focus:border-primary/50 w-full rounded border bg-transparent px-3 py-2 text-sm focus:outline-none"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-primary/50 text-xs font-medium">
              Ticket / reference ID
            </label>
            <input
              type="text"
              value={ticketRef}
              onChange={(e) => setTicketRef(e.target.value)}
              placeholder="e.g. SUP-4821"
              className="text-primary placeholder:text-primary/30 border-primary/20 focus:border-primary/50 w-full rounded border bg-transparent px-3 py-2 text-sm focus:outline-none"
            />
          </div>

          {isError && (
            <p className="text-xs text-red-400">
              Failed to start session — please try again.
            </p>
          )}

          <div className="pt-1">
            <Button type="submit" disabled={!canSubmit}>
              {isPending ? 'Starting…' : 'Start session'}
            </Button>
          </div>
        </form>
      </div>

      {/* recent sessions */}
      <div className="bg-bg-elevated rounded-lg border border-white/6 p-6">
        <p className="text-subtle mb-5 text-xs font-semibold tracking-widest uppercase">
          Recent Sessions
        </p>

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-9 animate-pulse rounded bg-white/6" />
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-muted text-sm">No sessions yet.</p>
        ) : (
          <div className="divide-y divide-white/6">
            {sessions.map((s) => (
              <div key={s.id} className="flex items-start gap-12 py-3">
                <span className="text-subtle w-24 shrink-0 text-xs tabular-nums">
                  {formatDate(s.createdAt)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-primary flex items-center gap-1 text-sm">
                    <span className="text-accent font-medium">
                      {s.operatorName}
                    </span>
                    <ArrowRight size={12} className="text-muted shrink-0" />
                    <span>{s.tenantName}</span>
                    {s.reason && (
                      <>
                        <span className="text-primary">·</span>
                        <span className="text-primary truncate">
                          "{s.reason}"
                        </span>
                      </>
                    )}
                    {s.ticketRef && (
                      <>
                        <span className="text-primary">·</span>
                        <span className="text-primary">{s.ticketRef}</span>
                      </>
                    )}
                  </p>
                  <p className="text-subtle mt-0.5 text-xs">
                    {formatDuration(s.durationMs)
                      ? `${formatDuration(s.durationMs)} · ended normally`
                      : 'active'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
